import { useParams } from 'react-router-dom';
import { ArrowRight, BookOpenText, ClipboardList, FileSpreadsheet, Scale, UserRoundSearch } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CtaPanel, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink } from '@/components/page/PageKit';
import { INTERVIEW_QUESTIONS_HUB, INTERVIEW_QUESTIONS_PATH, ROLE_SECTORS, ROLE_SETS, getRoleTemplate, roleQuestionCount, roleUrl } from '@/data/roleQuestions';
import { TemplatePage, type TemplateSection } from '@/pages/TemplateDetail';
import { useSEO } from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

const ROLE_SECTION: TemplateSection = {
  name: 'Interview questions',
  href: INTERVIEW_QUESTIONS_PATH,
  allLabel: 'See every role',
  stepsTitle: t => `How to run the interview with these ${t.name.toLowerCase()}`,
};

const GENERAL_SETS = [
  { title: 'Phone screening questions', body: '30 questions for the first call, for any role.', href: '/templates/phone-screening-questions' },
  { title: 'Competency-based interview questions', body: '30 examples across ten competencies, with a STAR scoring guide.', href: '/templates/competency-based-interview-questions' },
  { title: 'Strengths-based interview questions', body: '25 questions on what a candidate enjoys and does naturally.', href: '/templates/strengths-based-interview-questions' },
  { title: 'Reference check questions', body: '25 questions for a candidate’s former manager.', href: '/templates/reference-check-questions' },
];

export function RoleQuestionsPage({ slug }: { slug: string }) {
  const t = getRoleTemplate(slug);
  return t ? <TemplatePage t={t} section={ROLE_SECTION} /> : <NotFound />;
}

export function RoleQuestionsRoute() {
  const { role } = useParams<{ role: string }>();
  return <RoleQuestionsPage slug={role ?? ''} />;
}

const itemList = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Interview questions by role',
  itemListElement: ROLE_SETS.map((r, i) => ({ '@type': 'ListItem', position: i + 1, url: roleUrl(r.slug), name: r.keyword })),
};

export default function InterviewQuestions() {
  useSEO({ title: INTERVIEW_QUESTIONS_HUB.title, description: INTERVIEW_QUESTIONS_HUB.description, canonicalUrl: roleUrl() });
  return (
    <PageShell>
      <PageStructuredData name="Interview questions by role" description={INTERVIEW_QUESTIONS_HUB.description} url={roleUrl()} type="CollectionPage" breadcrumbs={[{ name: 'Interview questions' }]} />
      <StructuredData data={itemList} />
      <PageHero breadcrumbs={[{ name: 'Interview questions' }]} eyebrow="Free question sets" title={<>Interview questions by role, <span className="bv-hero-highlight">with a scorecard for each.</span></>} description="Questions written for the interviewer, not the candidate: what to ask, what a strong answer shows, what to verify and how to score it.">
        <InShort>{INTERVIEW_QUESTIONS_HUB.shortAnswer}</InShort>
      </PageHero>

      <Section tone="white" labelledBy="roles-title">
        <SectionHeading id="roles-title" eyebrow="By role" title="Pick the role you are hiring for" description="Each page includes the questions, red flags, a five-criterion scorecard and a CSV download." />
        <div className="mt-12 space-y-12">
          {ROLE_SECTORS.map(sector => {
            const roles = ROLE_SETS.filter(r => r.sector === sector);
            if (!roles.length) return null;
            return (
              <div key={sector}>
                <h3 className="font-display text-xl font-semibold text-bv-ink">{sector}</h3>
                <ul className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {roles.map((r, i) => (
                    <Reveal key={r.slug} delay={(i % 3) * 0.05}>
                      <li className="h-full">
                        <SmartLink href={`${INTERVIEW_QUESTIONS_PATH}/${r.slug}`} className="group flex h-full flex-col rounded-2xl border border-bv-blue-line bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-bv-blue/40 hover:shadow-bv-card">
                          <div className="flex items-center justify-between gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue"><UserRoundSearch className="h-5 w-5" /></span>
                            <span className="rounded-full bg-bv-blue-wash px-2.5 py-1 text-xs font-semibold text-bv-ink2">{roleQuestionCount(r)} questions · scorecard</span>
                          </div>
                          <h4 className="mt-4 font-display text-lg font-semibold leading-snug text-bv-ink">{r.keyword}</h4>
                          <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{r.description}</p>
                          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">Open the questions <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                        </SmartLink>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>

      <Section tone="wash" labelledBy="general-title">
        <SectionHeading id="general-title" eyebrow="For any role" title="General question sets" description="Use these alongside the role questions, or for roles not listed yet." />
        <ul className="mt-10 grid gap-4 md:grid-cols-2">
          {GENERAL_SETS.map(set => (
            <li key={set.href}>
              <SmartLink href={set.href} className="group flex h-full items-start gap-4 rounded-2xl border border-bv-blue-line bg-white p-5 transition-all hover:border-bv-blue/40 hover:shadow-bv-card">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue"><FileSpreadsheet className="h-5 w-5" /></span>
                <span><span className="block font-display text-lg font-semibold text-bv-ink">{set.title}</span><span className="mt-1 block text-[15px] leading-6 text-bv-ink2">{set.body}</span></span>
              </SmartLink>
            </li>
          ))}
        </ul>
      </Section>

      <RelatedLinks eyebrow="Keep going" title="Score answers consistently" tone="base" items={[
        { title: 'Interview rubric template', body: 'Anchored 1–5 scores you can adapt for any role.', href: '/templates/interview-rubric', icon: Scale },
        { title: 'Interview evaluation form', body: 'Record ratings, evidence and a clear recommendation.', href: '/templates/interview-evaluation-form', icon: ClipboardList },
        { title: 'All free templates', body: 'Screening, interviewing, references and agency templates.', href: '/templates', icon: BookOpenText },
      ]} />
      <CtaPanel title={<>Ask every applicant <span className="bv-hero-highlight">the same questions, automatically.</span></>} description="Beatview turns your questions into a structured AI interview and scores each answer with the reasoning shown. The Free plan covers one active job; AI interviews are on Pro." secondary={{ label: 'How AI interviews work', href: '/ai-interviews' }} />
    </PageShell>
  );
}
